import { useEffect, useMemo, useState } from 'react';

import { LandingCategory } from 'constants/enums';
import { checkSpecialMarketingPage } from 'constants/main';
import usePageData from 'hooks/usePageData';
import useScreenName from 'hooks/useScreenName';
import getStructuredLanguageList from 'utils/getStructuredLanguageList';
import extractPaths from 'utils/hubs/extractPaths';

import useLocalesStore from 'stores/localesStore';
import usePageDataStore from 'stores/pageDataStore';
import { shallow } from 'zustand/shallow';
import usePageLanguage from './usePageLanguage';
import usePageSlug from './usePageSlug';
import usePartnershipPage from './usePartnershipPage';

const usePageDataFetch = () => {
  const [renderPage, setRenderPage] = useState<boolean>(false);
  const { slug: pagePath } = usePageSlug();
  const { language } = usePageLanguage();
  const { cmsData, loading } = usePageDataStore(state => ({
    cmsData: state.pageData,
    loading: state.loading,
  }));
  const pageType = usePageDataStore(state => state.pageType);
  const setColorData = usePageDataStore(state => state.setColorData);
  const setColorPages = usePageDataStore(state => state.setColorPages);

  const pageData = usePageData(language, pagePath, cmsData);
  const screenName = useScreenName() || 'homepage';

  useEffect(() => {
    if (cmsData?.data?.category === 'color') {
      setColorData(cmsData?.data?.colorData);
      setColorPages(cmsData?.data?.colorPages);
    }
  }, [setColorPages, setColorData, cmsData?.data?.category]);

  const locales = useLocalesStore(x => x.locales, shallow);
  const pageSlug = useMemo(
    () =>
      // hub pagination logic
      extractPaths(
        pagePath,
        cmsData?.hubs?.map(c => c.slug),
      ),
    [cmsData?.hubs, pagePath],
  );

  const isPartnership =
    checkSpecialMarketingPage(screenName) || pageData?.category === LandingCategory.partnership;

  const storageDataUnits = useMemo(() => {
    return {
      isStorageFeature: !isPartnership,
      screenName,
      promoRequired: pageData?.promoRequired,
      scope: pageData?.premiumPopupPackage,
      isPartnership,
    };
  }, [isPartnership, pageData?.premiumPopupPackage, pageData?.promoRequired, screenName]);

  usePartnershipPage(pageData, isPartnership, setRenderPage);

  const languageList = useMemo(
    () => getStructuredLanguageList(pageData, locales),
    [locales, pageData],
  );

  return {
    pageData,
    screenName,
    storageDataUnits,
    languageList,
    pageSlug,
    renderPage,
    pageType,
    loading,
  };
};

export default usePageDataFetch;
